import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import { Button, Card, Field, Input, PageHeader, Select, Spinner } from "../components/ui.jsx";
import { useAuth } from "../auth.jsx";

const REGIONS = [
  ["fsn1", "Falkenstein (FSN1)"],
  ["nbg1", "Nuremberg (NBG1)"],
  ["hel1", "Helsinki (HEL1)"],
];
const formatBytes = (bytes) => bytes < 1024 ? `${bytes} B` : bytes < 1024 ** 2 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 ** 2).toFixed(1)} MB`;

export default function ObjectStorage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [region, setRegion] = useState("fsn1");
  const [projectId, setProjectId] = useState("");
  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [accessKeys, setAccessKeys] = useState([]);
  const [accessKeyName, setAccessKeyName] = useState("");
  const [dedicatedAccessKeyId, setDedicatedAccessKeyId] = useState("");
  const [dedicatedSecret, setDedicatedSecret] = useState("");
  const [policyKeyId, setPolicyKeyId] = useState("platform-default");
  const [buckets, setBuckets] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState("");
  const [objects, setObjects] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [newBucket, setNewBucket] = useState("");
  const [services, setServices] = useState([]);
  const [serviceUuid, setServiceUuid] = useState("");
  const [preset, setPreset] = useState("evidence");
  const [origin, setOrigin] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const uploadInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const refreshBuckets = useCallback(async (currentRegion = region) => {
    const list = await api.objectStorageBuckets(currentRegion);
    setBuckets(list);
    setSelectedBucket((current) => current && list.some((b) => b.name === current) ? current : "");
    return list;
  }, [region]);

  const refreshObjects = useCallback(async (bucket = selectedBucket, cursor = "") => {
    if (!bucket) { setObjects([]); setNextToken(null); return; }
    const page = await api.objectStorageObjects(bucket, region, cursor);
    setObjects((current) => cursor ? [...current, ...page.objects] : page.objects);
    setNextToken(page.nextToken);
  }, [region, selectedBucket]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    Promise.all([api.objectStorageStatus(), api.objectStorageServices(), api.objectStorageAccessKeys()])
      .then(([connection, serviceList, keyList]) => {
        setStatus(connection);
        if (connection.region) setRegion(connection.region);
        if (connection.projectId) setProjectId(connection.projectId);
        setServices(serviceList);
        setAccessKeys(keyList);
        if (serviceList[0]) setServiceUuid(serviceList[0].uuid);
      })
      .catch((e) => setError(e.message || "Could not load Object Storage settings."));
  }, [user?.role]);

  useEffect(() => {
    if (!status?.configured) return;
    refreshBuckets(region).catch((e) => setError(e.message || "Could not list buckets."));
  }, [status?.configured, region, refreshBuckets]);

  useEffect(() => {
    if (!selectedBucket) return;
    refreshObjects(selectedBucket).catch((e) => setError(e.message || "Could not list objects."));
  }, [selectedBucket, region, refreshObjects]);

  async function run(action, successMessage) {
    setBusy(true); setError(""); setNotice("");
    try { const result = await action(); if (successMessage) setNotice(successMessage(result)); return result; }
    catch (e) { setError(e.message || "The Object Storage request failed."); return null; }
    finally { setBusy(false); }
  }

  async function saveConnection(e) {
    e.preventDefault();
    const result = await run(() => api.saveObjectStorageConnection({ region, projectId, accessKeyId, secretAccessKey }), () => "Connected. Credentials are encrypted in the DebutDeploy database.");
    if (result) { setStatus(result); setAccessKeyId(""); setSecretAccessKey(""); }
  }

  async function createBucket(e) {
    e.preventDefault();
    if (!newBucket.trim()) return;
    const result = await run(() => api.createObjectStorageBucket({ name: newBucket.trim(), region }), (created) => created.warning
      ? `Private bucket created and restricted to this S3 key. ${created.warning}`
      : "Private bucket created and restricted to this S3 key.");
    if (result) { setNewBucket(""); await refreshBuckets(); setSelectedBucket(result.name); }
  }

  async function deleteBucket(bucket) {
    if (!window.confirm(`Delete empty bucket “${bucket}”? This cannot be undone.`)) return;
    const result = await run(() => api.deleteObjectStorageBucket(bucket, region), () => "Bucket deleted.");
    if (result) { if (selectedBucket === bucket) setSelectedBucket(""); await refreshBuckets(); }
  }

  async function restrictBucket(bucket) {
    const key = accessKeys.find((item) => item.id === policyKeyId);
    const result = await run(() => api.restrictObjectStorageBucket(bucket, { region, accessKeyId: policyKeyId }), () => `Bucket ${bucket} is now restricted to ${key?.name || "the selected key"}.`);
    return result;
  }

  async function addDedicatedKey(e) {
    e.preventDefault();
    const result = await run(() => api.addObjectStorageAccessKey({ name: accessKeyName, region, projectId, accessKeyId: dedicatedAccessKeyId, secretAccessKey: dedicatedSecret }), () => "Dedicated key verified and stored encrypted. The secret is cleared and cannot be retrieved from DebutDeploy.");
    if (result) {
      setAccessKeys(result);
      const added = result.find((item) => item.dedicated && item.accessKeyId.endsWith(dedicatedAccessKeyId.slice(-4)));
      if (added) setPolicyKeyId(added.id);
      setAccessKeyName(""); setDedicatedAccessKeyId(""); setDedicatedSecret("");
    }
  }

  async function setLifecycle(bucket) {
    await run(() => api.setObjectStorageLifecycle(bucket, { region }), () => `Unfinished multipart uploads in ${bucket} will be removed after 7 days.`);
  }

  async function upload(e) {
    e.preventDefault();
    if (!selectedBucket || !uploadFile) return;
    const file = uploadFile;
    const result = await run(() => api.uploadObjectStorageObject(selectedBucket, region, file.name, file), () => `Uploaded ${file.name}.`);
    if (result) { setUploadFile(null); if (uploadInput.current) uploadInput.current.value = ""; await refreshObjects(); }
  }

  async function deleteObject(key) {
    if (!window.confirm(`Permanently delete “${key}”? This cannot be undone.`)) return;
    const result = await run(() => api.deleteObjectStorageObject(selectedBucket, region, key), () => "Object deleted.");
    if (result) await refreshObjects();
  }

  async function attachBucket() {
    const result = await run(() => api.attachObjectStorageBucket(selectedBucket, { serviceUuid, region, kind: preset, accessKeyId: policyKeyId }), (r) => `Added ${r.variables.length} environment variables to ${r.service}. Redeploy that service to apply them.`);
    if (result) setServices((current) => current);
  }

  async function setCors() {
    await run(() => api.configureObjectStorageCors(selectedBucket, { region, origin: origin.trim() }), () => `Browser upload CORS set for ${origin.trim()}.`);
  }

  if (user?.role !== "admin") {
    return (
      <div className="page space-y-5">
        <PageHeader title="S3 Storage" subtitle="Object storage for your account and deployed services." />
        <Card>
          <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>Customer bucket management is being enabled</h2>
          <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
            S3 bucket creation and file management currently use the platform Hetzner connection, which is restricted to administrators. Your account cannot see or change another customer’s buckets.
          </p>
          <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
            Contact DebutDeploy support to provision a bucket and attach its connection settings to a service. Customer self-service requires account-scoped bucket records and a published storage price; Hetzner does not provide per-bucket usage totals through its S3 API.
          </p>
          <p className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}>
            If you need a bucket now, ask a DebutDeploy administrator to create one and attach it to the relevant service. Never put S3 access keys in source code or share them in chat.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      <PageHeader title="Object Storage" subtitle="Manage Hetzner buckets and objects, and connect buckets to deployed services." />
      <p className="-mt-4 text-xs" style={{ color: "var(--text-muted)" }}>Hetzner does not expose storage usage or traffic totals through its S3 API, so this page manages buckets and objects but does not show Object Storage charges.</p>

      {error && <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div role="status" className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</div>}

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Hetzner connection</h2>
            <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
              {status?.configured ? `Connected to ${status.endpoint} as ${status.accessKeyId}. The secret is stored encrypted.` : "Connect the Hetzner S3 key used for your bucket. Buckets created in the Hetzner Console will appear here after you save and test that key, in the same region. Hetzner does not offer an API for creating S3 keys."}
            </p>
          </div>
          {status?.configured && <span className="pill pill-ok">Connected</span>}
        </div>
        <form className="mt-5 grid gap-4 md:grid-cols-5" onSubmit={saveConnection}>
          <Field label="Region"><Select value={region} onChange={(e) => setRegion(e.target.value)}>{REGIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select></Field>
          <Field label="Hetzner project ID"><Input autoComplete="off" value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="Numeric project ID" /></Field>
          <Field label="Access key ID"><Input autoComplete="off" value={accessKeyId} onChange={(e) => setAccessKeyId(e.target.value)} placeholder={status?.configured ? "Re-enter current key" : "S3 access key"} /></Field>
          <Field label="Secret access key"><Input type="password" autoComplete="new-password" value={secretAccessKey} onChange={(e) => setSecretAccessKey(e.target.value)} placeholder={status?.configured ? "Re-enter key or enter replacement" : "S3 secret key"} /></Field>
          <div className="flex items-end"><Button variant="primary" disabled={busy || !projectId.trim() || !accessKeyId.trim() || !secretAccessKey.trim()}>{busy ? <Spinner /> : "Save and test"}</Button></div>
        </form>
        <p className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}>Hetzner keys are project-wide by default. Restrict each private bucket with a bucket policy before attaching its key to a service. The key is never shown after saving. Re-enter both values to update the project ID, region, or key; store a safe backup before rotating it.</p>
      </Card>

      <Card>
        <h2 className="text-base font-semibold">Dedicated bucket access keys</h2>
        <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>Hetzner does not provide an API to generate S3 keys. Create a key in the <a className="underline" style={{ color: "var(--accent-text)" }} href="https://console.hetzner.cloud/" target="_blank" rel="noreferrer">Hetzner Console → Security → S3 Credentials</a>, copy its secret once, then add it here. DebutDeploy verifies the key and encrypts it at rest.</p>
        <form className="mt-4 grid gap-3 md:grid-cols-5" onSubmit={addDedicatedKey}>
          <Field label="Key name"><Input value={accessKeyName} onChange={(e) => setAccessKeyName(e.target.value)} placeholder="TenderIn uploads" /></Field>
          <Field label="Access key ID"><Input autoComplete="off" value={dedicatedAccessKeyId} onChange={(e) => setDedicatedAccessKeyId(e.target.value)} /></Field>
          <Field label="Secret access key"><Input type="password" autoComplete="new-password" value={dedicatedSecret} onChange={(e) => setDedicatedSecret(e.target.value)} /></Field>
          <Field label="Region"><Select value={region} onChange={(e) => setRegion(e.target.value)}>{REGIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select></Field>
          <div className="flex items-end"><Button variant="primary" disabled={busy || !accessKeyName.trim() || !dedicatedAccessKeyId.trim() || !dedicatedSecret.trim() || !projectId.trim()}>{busy ? <Spinner /> : "Verify and add key"}</Button></div>
        </form>
        {!!accessKeys.length && <div className="mt-4 max-w-xl"><Field label="Key to allow on bucket policy"><Select value={policyKeyId} onChange={(e) => setPolicyKeyId(e.target.value)}>{accessKeys.map((key) => <option key={key.id} value={key.id}>{key.name} ({key.accessKeyId})</option>)}</Select></Field></div>}
      </Card>

      {status?.configured && <>
        <Card>
          <div className="flex flex-wrap items-end justify-between gap-4">
              <div><h2 className="text-base font-semibold">Buckets</h2><p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>The project bucket list is shared across regions. Select a bucket&apos;s region before opening its objects. A bucket can be deleted only when it is empty.</p></div>
            <Field label="Region"><Select value={region} onChange={(e) => setRegion(e.target.value)}>{REGIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select></Field>
          </div>
              <form className="mt-4 flex flex-wrap gap-2" onSubmit={createBucket}>
            <Input className="min-w-64 flex-1" value={newBucket} onChange={(e) => setNewBucket(e.target.value)} placeholder="new-private-bucket" aria-label="New bucket name" />
            <Button variant="primary" disabled={busy || !newBucket.trim()}>Create bucket</Button>
            <Button type="button" disabled={busy} onClick={() => run(() => refreshBuckets(), () => "Bucket list refreshed.")}>Refresh</Button>
          </form>
          <div className="mt-4 divide-y" style={{ borderColor: "var(--border)" }}>
            {buckets.length === 0 ? <p className="py-5 text-sm" style={{ color: "var(--text-muted)" }}>No buckets found in {region}.</p> : buckets.map((bucket) => (
              <div key={bucket.name} className="flex flex-wrap items-center gap-3 py-3">
                <button className="min-w-0 flex-1 truncate text-left text-sm font-semibold" style={{ color: "var(--accent-text)" }} onClick={() => setSelectedBucket(bucket.name)}>{bucket.name}</button>
                <span className="text-xs" style={{ color: "var(--text-muted)" }}>{bucket.createdAt ? new Date(bucket.createdAt).toLocaleDateString() : "Hetzner Object Storage"}</span>
                <Button disabled={busy || !accessKeys.length} onClick={() => restrictBucket(bucket.name)}>Restrict to selected key</Button>
                <Button disabled={busy} onClick={() => setLifecycle(bucket.name)}>Clean incomplete uploads after 7 days</Button>
                <Button variant="danger" disabled={busy} onClick={() => deleteBucket(bucket.name)}>Delete empty bucket</Button>
              </div>
            ))}
          </div>
        </Card>

        {selectedBucket && <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><h2 className="truncate text-base font-semibold">{selectedBucket}</h2><p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>Private object browser · {region}</p></div>
            <a className="text-sm underline" style={{ color: "var(--accent-text)" }} href={`https://${selectedBucket}.${region}.your-objectstorage.com`} target="_blank" rel="noreferrer">Bucket endpoint</a>
          </div>

          <div className="mt-5 grid gap-5 xl:grid-cols-2">
            <form onSubmit={upload} className="space-y-3">
              <h3 className="text-sm font-semibold">Upload object</h3>
              <input ref={uploadInput} className="input" type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>Uploads are limited to 200 MiB and use the file name as the object key.</p>
              <Button variant="primary" disabled={busy || !uploadFile}>{busy ? <Spinner /> : "Upload"}</Button>
            </form>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Browser upload access</h3>
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>Allow one exact HTTPS origin and expose ETag for multipart upload clients.</p>
              <div className="flex gap-2"><Input className="min-w-0 flex-1" value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="https://app.example.com" aria-label="Allowed browser origin" /><Button disabled={busy || !origin.trim()} onClick={setCors}>Set CORS</Button></div>
            </div>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-left text-sm"><thead><tr style={{ color: "var(--text-muted)" }}><th className="py-2 pr-3">Object key</th><th className="py-2 pr-3">Size</th><th className="py-2 pr-3">Updated</th><th className="py-2 text-right">Actions</th></tr></thead>
              <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                {objects.map((object) => <tr key={object.key}><td className="max-w-xs truncate py-3 pr-3 font-mono text-xs" title={object.key}>{object.key}</td><td className="whitespace-nowrap py-3 pr-3">{formatBytes(object.size)}</td><td className="whitespace-nowrap py-3 pr-3">{object.lastModified ? new Date(object.lastModified).toLocaleString() : "—"}</td><td className="space-x-2 whitespace-nowrap py-3 text-right"><a className="text-sm underline" style={{ color: "var(--accent-text)" }} href={`/api/admin/object-storage/buckets/${encodeURIComponent(selectedBucket)}/download?region=${encodeURIComponent(region)}&key=${encodeURIComponent(object.key)}`} target="_blank" rel="noreferrer">Download</a><Button variant="danger" disabled={busy} onClick={() => deleteObject(object.key)}>Delete</Button></td></tr>)}
                {!objects.length && <tr><td colSpan="4" className="py-5 text-center text-sm" style={{ color: "var(--text-muted)" }}>This bucket is empty.</td></tr>}
              </tbody>
            </table>
          </div>
          {nextToken && <div className="mt-4 text-center"><Button disabled={busy} onClick={() => run(() => refreshObjects(selectedBucket, nextToken))}>Load more objects</Button></div>}
        </Card>}

        {selectedBucket && <Card>
          <h2 className="text-base font-semibold">Attach bucket to a service</h2>
          <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>Adds the selected key and bucket details as encrypted service environment variables. Then redeploy the service to apply them.</p>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <Field label="Service"><Select value={serviceUuid} onChange={(e) => setServiceUuid(e.target.value)}><option value="">Choose service</option>{services.map((s) => <option key={s.uuid} value={s.uuid}>{s.name}{s.domain ? ` · ${s.domain}` : ""}</option>)}</Select></Field>
            <Field label="Variable names"><Select value={preset} onChange={(e) => setPreset(e.target.value)}><option value="evidence">Multimedia evidence (EVIDENCE_S3_*)</option><option value="aws">AWS SDK (AWS_*)</option><option value="tenderin-raw-payload">TenderIn raw payload store (RAW_PAYLOAD_S3_*)</option></Select></Field>
            <div className="flex items-end"><Button variant="primary" disabled={busy || !serviceUuid} onClick={attachBucket}>Attach bucket</Button></div>
          </div>
        </Card>}
      </>}
    </div>
  );
}
